import { type AxiosError, type AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import { call, put, select, take, takeEvery, takeLatest } from 'redux-saga/effects';

import { userDidAuthenticate } from '~/actions/auth.actions';
import { ActionKeys } from '~/actions/constants';
import { fetchFeatureTests } from '~/actions/feature-tests.actions';
import { countOrdersFetch } from '~/actions/orders.actions';
import { fetchUserPlans } from '~/actions/plans.actions';
import { applyOrReplacePromoCode } from '~/actions/promo.actions';
import * as userActions from '~/actions/user.actions';
import { BRANDING_UPDATED, COMPANY_UPDATED } from '~/constants/gtm';
import { ID_VERIFICATION_KEY } from '~/constants/query-string-keys';
import { LABEL_SETTINGS_QUERY_KEY } from '~/modules/settingsCarriers/constants/labelSettings.const';
import { type NextPaymentModalAction } from '~/reducers/user/user.reducer';
import { getSelectedOrders } from '~/selectors/orders.selectors';
import { getPathname } from '~/selectors/router.selectors';
import {
  getCompanySettings,
  getNextModalAction,
  getRequiresEmailVerification,
} from '~/selectors/user.selectors';
import * as entitlementsService from '~/services/entitlements.service';
import { pushToGTMDataLayer, sendGTMEvent } from '~/services/gtm.service';
import settingsService from '~/services/settings.service';
import * as userService from '~/services/user.service';
import { type ApplicationState } from '~/store';
import * as Asset from '~/types/Asset';
import * as Company from '~/types/Company';
import { fromJSON as entitlementsFromJSON, type JSONEntitlements } from '~/types/Entitlements';
import { IDVerificationStatus } from '~/types/IDVerification';
import { fromJSON, type JSONUser } from '~/types/User';
import { defaultQueryClient } from '~/utils/ShippoQueryClientProvider';
import { getCookie } from '~/utils/cookies';
import { setCookie } from '~/utils/marketing-source-attribution';
import { getTopLevelDomain } from '~/utils/url';

import { selectState } from '..';

export const getUserJWT = () => getCookie('jwt');

export const shouldAuthenticate = (state: ApplicationState) => !state.user.isAuthenticated;

export function* fetchUser() {
  try {
    const response: AxiosResponse<JSONUser> = yield call(userService.getCurrentUser);
    const payload: userActions.FetchUserPayload = {
      user: fromJSON(response.data),
    };

    // User has not yet authenticated, so dispatch userDidAuthenticate
    const willAuthenticate: boolean = yield selectState<boolean>(shouldAuthenticate);
    if (willAuthenticate) {
      yield put(userDidAuthenticate(payload));
      yield call(() =>
        defaultQueryClient.prefetchQuery({
          queryFn: settingsService.fetchLabelSettings,
          queryKey: LABEL_SETTINGS_QUERY_KEY,
        }),
      );
    }

    yield call(pushToGTMDataLayer, {
      group_jwt: response.data.group_jwt,
    });

    yield put(userActions.fetchUserSuccess(payload));
    yield put(fetchFeatureTests());
    yield put(fetchUserPlans());
    yield put(countOrdersFetch());

    if (!isSameSession()) {
      const hasPurchasedLabelCookie = 'customer';
      setCookie(hasPurchasedLabelCookie, 'true');
    }

    // Apply promo if pending
    const promocodeCookieValue = Cookies.get('shippo_apply_promo');
    if (promocodeCookieValue) {
      const decodedCookieValue = decodeURIComponent(promocodeCookieValue);
      const cookieDomain = getTopLevelDomain(window.location.href);
      if (payload.user.currentPromo && payload.user.currentPromo.code !== decodedCookieValue)
        yield put(applyOrReplacePromoCode(decodedCookieValue, true));
      else if (!payload.user.currentPromo)
        yield put(applyOrReplacePromoCode(decodedCookieValue, false));

      Cookies.remove('shippo_apply_promo', { domain: `.${cookieDomain}` });
      yield put(userActions.showPromoApplicationNotice());
      yield put(userActions.refreshUser());
    }
  } catch (err) {
    yield put(userActions.fetchUserError());
  }
}

export function* fetchUserEntitlements() {
  try {
    const response: AxiosResponse<JSONEntitlements> = yield call(
      entitlementsService.getEntitlements,
    );
    const payload = entitlementsFromJSON(response.data);
    yield put(userActions.fetchUserEntitlementsSuccess(payload));
  } catch (err) {
    yield put(userActions.fetchUserEntitlementsError());
  }
}

function* fetchCompany() {
  try {
    const response: AxiosResponse<Company.JSONCompany> = yield call(userService.fetchCompany);
    yield put(userActions.fetchCompanySuccess(Company.fromJSON(response.data)));
  } catch (err) {
    yield put(userActions.fetchCompanyError());
  }
}

export function* refreshUser() {
  try {
    const response: AxiosResponse<JSONUser> = yield call(userService.getCurrentUser);
    const payload: userActions.FetchUserPayload = {
      user: fromJSON(response.data),
    };
    yield put(userActions.fetchUserSuccess(payload));
  } catch (e) {
    yield put(userActions.fetchUserError());
  }
}

export function* revokeAuth() {
  // Same logic as used in django to log a user out
  yield call(Cookies.remove, 'jwt', { domain: CONFIG.shippoRootUrl, path: '/' });
  yield call(Cookies.remove, 'is_logged_in', { domain: CONFIG.shippoRootUrl, path: '/' });
  yield put(userActions.userSetUnauthenticated());
}

function* fetchAssets() {
  try {
    const response: AxiosResponse<{ results: Asset.JSONAsset[] }> = yield call(
      userService.fetchAssets,
    );
    const assets = response.data.results.map(Asset.fromJSON);
    yield put(userActions.fetchAssetsSuccess(assets));
  } catch (e) {
    yield put(userActions.fetchAssetsError('unknown_error'));
  }
}

function* uploadAssetDraft(action: ReturnType<typeof userActions.uploadAssetDraft>) {
  const { assetType, rank } = action.payload;
  try {
    const { asset, file } = action.payload;

    let fullAsset = asset;

    if (!asset) {
      // Create asset if asset no asset is present
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, {
        draftImageUrl: '',
        imageUrl: '',
        linkUrl: null,
        rank,
        title: null,
        type: assetType,
      });

      fullAsset = Asset.fromJSON(result.data);
    }

    yield call(userService.uploadAssetDraft, fullAsset?.objectId!, file);
    yield put(userActions.fetchAssets());
    yield put(userActions.uploadAssetDraftSuccess(assetType, rank));
  } catch (e) {
    const err = e as AxiosError;

    const errCode = err.response!.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.uploadAssetDraftError(assetType, rank, errCode));
  }
}

function* clearAssetDraft(action: ReturnType<typeof userActions.clearAssetDraft>) {
  const { asset, assetType, rank } = action.payload;
  try {
    let fullAsset: Asset.Asset;

    if (!asset.objectId) {
      // Create asset if asset doesn't have an objectId
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, {
        ...asset,
        rank,
        type: assetType,
      });

      fullAsset = Asset.fromJSON(result.data);
    } else {
      // If it has an objectId it is a complete Asset. So we coerce the type.
      fullAsset = asset as Asset.Asset;
    }

    yield call(userService.clearAssetDraft, fullAsset.objectId);
    yield put(userActions.fetchAssets());
    yield put(userActions.clearAssetDraftSuccess(assetType, rank));
  } catch (e) {
    const err = e as AxiosError;

    const errCode = err?.response?.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.clearAssetDraftError(assetType, rank, errCode));
  }
}

function* revertAssetDraft(action: ReturnType<typeof userActions.clearAssetDraft>) {
  const { asset, assetType, rank } = action.payload;
  try {
    let fullAsset: Asset.Asset;

    if (!asset.objectId) {
      // Create asset if asset doesn't have an objectId
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, {
        ...asset,
        rank,
        type: assetType,
      });

      fullAsset = Asset.fromJSON(result.data);
    } else {
      // If it has an objectId it is a complete Asset. So we coerce the type.
      fullAsset = asset as Asset.Asset;
    }

    // check if there is a live image to revert to in the first place
    yield call(userService.revertAssetDraft, fullAsset.objectId);
    yield put(userActions.fetchAssets());
    yield put(userActions.revertAssetDraftSuccess(assetType, rank));
  } catch (e) {
    const err = e as AxiosError;

    const errCode = err?.response?.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.revertAssetDraftError(assetType, rank, errCode));
  }
}

function* publishAsset(action: ReturnType<typeof userActions.publishAsset>) {
  const { assetType, rank } = action.payload;
  try {
    const { asset } = action.payload;

    let fullAsset: Asset.Asset;

    if (!asset.objectId) {
      // Create asset if asset doesn't have an objectId
      const result: AxiosResponse<Asset.JSONAsset> = yield call(userService.createAsset, asset);

      fullAsset = Asset.fromJSON(result.data);
    } else {
      // If it has an objectId it is a complete Asset. So we coerce the type.
      fullAsset = asset as Asset.Asset;
    }

    // If there is a difference between the published and draft images, publish the draft
    if (fullAsset.draftImageUrl !== fullAsset.imageUrl)
      yield call(userService.publishAssetDraft, fullAsset.objectId);

    // Update asset with new info (title, link, etc)
    yield call(userService.updateAsset, fullAsset);
    yield put(userActions.publishAssetSuccess(assetType, rank));
    yield put(userActions.fetchAssets());
  } catch (e) {
    const err = e as AxiosError;
    let errCode;
    if (err?.response?.status === 400 && err?.response?.data?.link_url) errCode = 'invalid_link';
    else errCode = err?.response?.status === 415 ? 'invalid_file_type' : 'unknown_error';

    yield put(userActions.publishAssetError(assetType, rank, errCode));
  }
}

function* uploadAndPublishLogo(action: ReturnType<typeof userActions.uploadAndPublishLogo>) {
  try {
    const { file } = action.payload;

    yield call(userService.uploadAndPublishAsset, Asset.AssetType.LOGO, file);
    yield put(userActions.uploadAndPublishLogoSuccess());
    yield call(sendGTMEvent, { eventName: BRANDING_UPDATED });
    yield put(userActions.fetchAssets());
  } catch (e) {
    const err = e as AxiosError;

    let errCode = 'unknown_error';

    if (err.response && err.response.status === 400 && err.response?.data.errors) {
      errCode = err.response.data.errors.reduce(
        (acc: string, cur: { code: string }) =>
          acc ? [acc, cur.code].join(';') : (cur.code as string),
        '',
      );
    }

    yield put(userActions.uploadAndPublishLogoError(errCode));
  }
}

function* updateBrandColors(action: ReturnType<typeof userActions.updateBrandColors>) {
  try {
    // PUT to the /settings/company endpoint requires all fields, but we're only changing brand colors, so populate
    // the fields that aren't changing from the store
    const updatedCompanySettings: Maybe<Company.Company> =
      yield selectState<Maybe<Company.Company>>(getCompanySettings);
    // What happens if this is empty?
    // Try to do it without `!`
    updatedCompanySettings!.brandPrimaryColor = action.payload.brandPrimaryColor;
    updatedCompanySettings!.brandSecondaryColor = action.payload.brandSecondaryColor;

    yield call(userService.updateCompany, Company.toJSON(updatedCompanySettings!));
    yield put(userActions.updateBrandColorsSuccess());
    yield call(sendGTMEvent, { eventName: BRANDING_UPDATED });
    yield put(userActions.fetchCompany());
  } catch (e) {
    yield put(userActions.updateBrandColorsError());
  }
}

const isSameSession = () => {
  const jwt = getUserJWT();
  const copy = getCookie('spare');
  if (!copy || jwt !== copy) {
    setCookie('spare', jwt);
    return false;
  }
  return true;
};

export function* initiateIdVerification(
  action: ReturnType<typeof userActions.initiateVerification>,
) {
  try {
    const selectedOrders = yield select(getSelectedOrders);
    const orders = Object.keys(selectedOrders);

    const currentPathname: string = yield selectState<string>(getPathname);
    const basePath = action.payload.returnUrl || currentPathname;
    let urlString = `${basePath}?${ID_VERIFICATION_KEY}=true`;

    if (orders.length) {
      const ordersString = orders.join(',');

      urlString = urlString + `&selected_orders=` + ordersString;
    }

    const url = new URL(urlString, CONFIG.reactBaseUrl);
    const returnUrl = url.toString();

    const result: AxiosResponse<{ redirect_url: string }> = yield call(
      userService.createVerificationIntent,
      returnUrl,
    );
    yield call([window.location, window.location.assign], result.data.redirect_url);
  } catch (err) {
    // I'm going to cheat.
    // Instead of saving error state somewhere I'm going to use
    // the id verification state to set an error with a fake IDVerification.
    // So if any error happens we can display the verification
    // failed modal with a generic message that the user can retry.
    yield put(
      userActions.setIdVerificationStatus({
        errors: ['error_processing_request'],
        state: IDVerificationStatus.VERIFICATION_FAILED_ALLOW_RETRY,
      }),
    );
  }
}

export function* maybeShowEmailVerificationModal(
  action: ReturnType<typeof userActions.maybeShowEmailVerificationModal>,
) {
  const requiresEmailVerification: boolean = yield selectState<boolean>(
    getRequiresEmailVerification,
  );

  if (requiresEmailVerification)
    yield put(userActions.showEmailVerificationModal(action.payload.nextModalAction));
  else if (action.payload.nextModalAction) yield put(action.payload.nextModalAction);
}

export function* checkRequiresEmailVerification(
  action: ReturnType<typeof userActions.checkRequiresEmailVerification>,
) {
  yield put(userActions.refreshUser());
  yield take([ActionKeys.USER_FETCH_SUCCESS, ActionKeys.USER_FETCH_ERROR]);

  const requiresEmailVerification: boolean = yield selectState<boolean>(
    getRequiresEmailVerification,
  );

  if (!requiresEmailVerification) {
    // Must come first, verifySuccessCallback() clears nextModalAction state
    const nextModalAction: Maybe<NextPaymentModalAction> =
      yield selectState<Maybe<NextPaymentModalAction>>(getNextModalAction);

    yield put(userActions.emailVerificationSuccess());
    yield call(action.payload.verifySuccessCallback);

    if (nextModalAction) yield put(nextModalAction);
  }
}

function* sendGTMEventOnCompanyUpdateSuccess() {
  yield call(sendGTMEvent, { eventName: COMPANY_UPDATED });
}

export default [
  takeLatest(ActionKeys.USER_FETCH, fetchUser),
  takeLatest(ActionKeys.USER_ENTITLEMENTS_FETCH, fetchUserEntitlements),
  takeLatest(ActionKeys.COMPANY_UPDATE_SUCCESS, sendGTMEventOnCompanyUpdateSuccess),
  takeLatest(ActionKeys.COMPANY_FETCH, fetchCompany),
  takeLatest(ActionKeys.USER_REFRESH, refreshUser),
  takeLatest(ActionKeys.REVOKE_AUTH, revokeAuth),
  takeLatest(ActionKeys.USER_ASSETS_FETCH, fetchAssets),
  takeEvery(ActionKeys.USER_ASSET_UPLOAD_DRAFT, uploadAssetDraft),
  takeEvery(ActionKeys.USER_ASSET_CLEAR_DRAFT, clearAssetDraft),
  takeEvery(ActionKeys.USER_ASSET_REVERT_DRAFT, revertAssetDraft),
  takeEvery(ActionKeys.USER_ASSET_PUBLISH, publishAsset),
  takeLatest(ActionKeys.USER_INITIATE_ID_VERIFICATION, initiateIdVerification),
  takeLatest(ActionKeys.USER_ASSET_UPLOAD_AND_PUBLISH_LOGO, uploadAndPublishLogo),
  takeLatest(ActionKeys.USER_BRAND_UPDATE_COLORS, updateBrandColors),
  takeLatest(ActionKeys.MAYBE_SHOW_EMAIL_VERIFICATION_MODAL, maybeShowEmailVerificationModal),
  takeLatest(ActionKeys.CHECK_REQUIRES_EMAIL_VERIFICATION, checkRequiresEmailVerification),
];
